/**
 * GL state cache and coordinate helpers (W2-01a preview-gl; ARCHITECTURE.md §7.3, rendering.md R§1.1, R§4.4).
 *
 * Blending: Phira draws everything with straight (non-premultiplied) alpha, `SRC_ALPHA / ONE_MINUS_SRC_ALPHA`, and
 * keeps the destination alpha (S§4.2), so the colour channels blend straight while the alpha channel of a render
 * target stays what the opaque background wrote (1). Persistent paint buffers are layers composited over the
 * frame, so they are kept premultiplied (`premultiplied`); effect passes and copies replace (`none`).
 *
 * Coordinates: every batch draws in some 2D "space" and a 2x3 matrix maps it to clip space. `clipFromPx` maps
 * device pixels of a target (y down, origin top-left) to clip space; rendering into a framebuffer object with that
 * matrix stores the top of the image in the texture's LAST row, so FBO contents are always composited with
 * texture t = 1 at the top edge and the GL convention of effects (`uv` (0, 0) = bottom-left) holds.
 */

import type { Mat2D, MutMat2D } from '../../core/util/types';

export type BlendMode = 'none' | 'alpha' | 'premultiplied';

/**
 * The result of a `gl.create*` call as nullable: after a context loss the browser returns null although the DOM
 * typings say otherwise.
 */
export function glObject<T>(v: T): T | null {
  return v;
}

/** A rectangle in device pixels of a render target, origin top-left (y down). */
export interface PxRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** Device pixels (y down) of a `w` x `h` target -> clip space (y up). */
export function clipFromPx(w: number, h: number): Mat2D {
  const sw = w > 0 ? w : 1;
  const sh = h > 0 ? h : 1;
  return [2 / sw, 0, 0, -2 / sh, -1, 1];
}

/** `a · b` (apply `b` first). */
export function mulMat(a: Mat2D, b: Mat2D, out: MutMat2D = [1, 0, 0, 1, 0, 0]): MutMat2D {
  const a0 = a[0];
  const a1 = a[1];
  const a2 = a[2];
  const a3 = a[3];
  const a4 = a[4];
  const a5 = a[5];
  const b0 = b[0];
  const b1 = b[1];
  const b2 = b[2];
  const b3 = b[3];
  const b4 = b[4];
  const b5 = b[5];
  out[0] = a0 * b0 + a2 * b1;
  out[1] = a1 * b0 + a3 * b1;
  out[2] = a0 * b2 + a2 * b3;
  out[3] = a1 * b2 + a3 * b3;
  out[4] = a0 * b4 + a2 * b5 + a4;
  out[5] = a1 * b4 + a3 * b5 + a5;
  return out;
}

/** Column-major mat3 of a 2x3 affine matrix (for `uniformMatrix3fv`). */
export function mat3Of(m: Mat2D, out: Float32Array = new Float32Array(9)): Float32Array {
  out[0] = m[0];
  out[1] = m[1];
  out[2] = 0;
  out[3] = m[2];
  out[4] = m[3];
  out[5] = 0;
  out[6] = m[4];
  out[7] = m[5];
  out[8] = 1;
  return out;
}

/** True when two matrices are bit-identical. */
export function sameMat(a: Mat2D, b: Mat2D): boolean {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3] && a[4] === b[4] && a[5] === b[5];
}

/** Integer pixel rectangle (scissor / blit) of a fractional one, clamped to the target. */
export function snapRect(r: PxRect, targetW: number, targetH: number): PxRect {
  const x0 = Math.max(0, Math.min(targetW, Math.round(r.x)));
  const y0 = Math.max(0, Math.min(targetH, Math.round(r.y)));
  const x1 = Math.max(x0, Math.min(targetW, Math.round(r.x + r.w)));
  const y1 = Math.max(y0, Math.min(targetH, Math.round(r.y + r.h)));
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/**
 * Cached GL state. Every pass changes state only through this object, so redundant calls are skipped and the
 * cache can be dropped as a whole after a context restore (`reset`).
 */
export class GlState {
  readonly gl: WebGL2RenderingContext;
  private blend: BlendMode | null = null;
  private fbo: WebGLFramebuffer | null | undefined = undefined;
  private targetW = 0;
  private targetH = 0;
  private program: WebGLProgram | null | undefined = undefined;
  private scissor: PxRect | null | undefined = undefined;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
  }

  /** Forget everything (context restored, or foreign code touched the context). */
  reset(): void {
    this.blend = null;
    this.fbo = undefined;
    this.targetW = 0;
    this.targetH = 0;
    this.program = undefined;
    this.scissor = undefined;
  }

  setBlend(mode: BlendMode): void {
    if (this.blend === mode) return;
    const gl = this.gl;
    if (mode === 'none') gl.disable(gl.BLEND);
    else {
      gl.enable(gl.BLEND);
      gl.blendEquation(gl.FUNC_ADD);
      if (mode === 'alpha') gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ZERO, gl.ONE);
      else gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    }
    this.blend = mode;
  }

  /** Binds a draw target (null = the canvas) and sets the viewport to all of it; scissoring is turned off. */
  bindTarget(fbo: WebGLFramebuffer | null, w: number, h: number): void {
    const gl = this.gl;
    if (this.fbo !== fbo) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      this.fbo = fbo;
    }
    if (this.targetW !== w || this.targetH !== h) {
      gl.viewport(0, 0, w, h);
      this.targetW = w;
      this.targetH = h;
    }
    this.setScissor(null);
  }

  /** Forget the bound framebuffer (code outside this cache bound READ / DRAW framebuffers, e.g. a blit). */
  forgetTarget(): void {
    this.fbo = undefined;
  }

  /** Current draw target size in pixels. */
  get target(): { readonly w: number; readonly h: number } {
    return { w: this.targetW, h: this.targetH };
  }

  /** Restrict drawing to `r` (device pixels of the bound target, y down); null = no scissor. */
  setScissor(r: PxRect | null): void {
    const gl = this.gl;
    const cur = this.scissor;
    if (r === null) {
      if (cur !== null) gl.disable(gl.SCISSOR_TEST);
      this.scissor = null;
      return;
    }
    const s = snapRect(r, this.targetW, this.targetH);
    if (cur !== null && cur !== undefined && cur.x === s.x && cur.y === s.y && cur.w === s.w && cur.h === s.h) return;
    if (cur === null || cur === undefined) gl.enable(gl.SCISSOR_TEST);
    // GL scissor rectangles are measured from the bottom-left corner.
    gl.scissor(s.x, this.targetH - s.y - s.h, s.w, s.h);
    this.scissor = s;
  }

  useProgram(p: WebGLProgram | null): void {
    if (this.program === p) return;
    this.gl.useProgram(p);
    this.program = p;
  }

  /** Clear the bound target to an opaque colour (scissor respected). */
  clear(r: number, g: number, b: number, a: number): void {
    const gl = this.gl;
    gl.clearColor(r, g, b, a);
    gl.clear(gl.COLOR_BUFFER_BIT);
  }
}
